import React, { useState } from "react";
import moment, { Moment } from "moment";
import { intl } from "LocaleWrapper";
import { shortid } from "imports/Utils";
import { Item, LocationFromQuery, Window } from "./LocationPlanning";
import { Box, Menu, MenuItem, Tooltip } from "@mui/material";
import { Day } from "types/enums";
import {
	LocationPlanningLocationAvailabilityFragment,
	LocationPlanningPlanboardQuayPlanningFragment,
} from "__generated__/view/locationplanning/graphql/locationplanning.fragments.generated";
import { DateProperty, DateUtils } from "utils/DateUtils";

export const createQuayPlanningTasks = (
	quayPlannings: LocationPlanningPlanboardQuayPlanningFragment[],
	editedQuayPlanning: {
		default: LocationPlanningPlanboardQuayPlanningFragment,
		new: {
			start: number,
			end: number,
			quayId: number,
		},
	} | null,
	enableResizing: (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => void,
	openSideViewer: (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => void,
	unplan: (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => void,
): Item[] => {
	return quayPlannings.sort((a, b) => {
		if (a.quay.id === b.quay.id) {
			return a.start - b.start;
		}
		return a.quay.id - b.quay.id;
	}).map((quayPlanning, index, self) => {
		// Check if are adjoining, meaning have the same quay and start/end within 5 seconds of each other
		const hasNext = index < self.length - 1 && self[index + 1].quay.id === quayPlanning.quay.id && (self[index + 1].start - quayPlanning.end) < 5000;
		const hasPrevious = index > 0 && self[index - 1].quay.id === quayPlanning.quay.id && (quayPlanning.start - self[index - 1].end) < 5000;

		const base: Pick<Item, "id" | "entity" | "description" | "title"> = {
			id: `quayPlanning-${quayPlanning.id}`,
			entity: quayPlanning,
			description: "",
			title: (
				<QuayPlanningTaskContent
					quayPlanning={quayPlanning}
					enableResizing={enableResizing}
					openSideviewer={openSideViewer}
					unplan={unplan}
				/>
			),
		};
		if (quayPlanning.id === editedQuayPlanning?.default.id) {
			return {
				...base,
				group: `location_${quayPlanning.cargoAction.location?.id}_quay_${editedQuayPlanning.new.quayId}`,
				start: moment(editedQuayPlanning.new.start),
				end: moment(editedQuayPlanning.new.end),
				className: "quayPlanning being-edited",
				canMove: true,
				canResize: "both",
			};
		} else {
			return {
				...base,
				group: `location_${quayPlanning.cargoAction.location?.id}_quay_${quayPlanning.quay.id}`,
				start: moment(quayPlanning.start),
				end: moment(quayPlanning.end),
				className: "quayPlanning" + (hasPrevious ? " has-previous" : "") + (hasNext ? " has-next" : ""),
			};
		}
	});
};

const QuayPlanningTaskContent = ({
	quayPlanning,
	enableResizing,
	openSideviewer,
	unplan,
}: {
	quayPlanning: LocationPlanningPlanboardQuayPlanningFragment,
	enableResizing: (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => void,
	openSideviewer: (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => void,
	unplan: (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => void,
}) => {

	const [ menu, setMenu ] = useState<{ mouseX: number; mouseY: number; } | null>(null);

	const description = getQuayPlanningDescription(quayPlanning);

	const handleOnClose = () => {
		setMenu(null);
	};

	return (
		<React.Fragment>
			<div
				id={`quayPlanning-${quayPlanning.id}-item`}
				onClick={(event) => {
					event.preventDefault();
					setMenu({ mouseX: event.clientX + 2, mouseY: event.clientY + 4 });
				}}
				className="isCall">
				<Tooltip title={description}>
					<Box
						sx={{
							display: "block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							width: "100%",
						}}
						pl={0.5}
						pr={0.5}>
						{description}
					</Box>
				</Tooltip>
			</div>

			<Menu
				open={menu !== null}
				onClose={handleOnClose}
				anchorReference="anchorPosition"
				anchorPosition={menu !== null ? {
					top: menu.mouseY,
					left: menu.mouseX,
				} : undefined}
			>
				<MenuItem style={{ display: "none" }} />

				<MenuItem
					onClick={() => {
						handleOnClose();
						enableResizing(quayPlanning);
					}}
					dense
				>
					{intl.formatMessage({ id: "EDIT_IN_PLANBOARD" })}
				</MenuItem>

				<MenuItem
					onClick={() => {
						handleOnClose();
						openSideviewer(quayPlanning);
					}}
					dense
				>
					{intl.formatMessage({ id: "OPEN_SIDEVIEWER" })}
				</MenuItem>

				<MenuItem
					onClick={() => {
						handleOnClose();
						unplan(quayPlanning);
					}}
					dense
				>
					{intl.formatMessage({ id: "DELETE" })}
				</MenuItem>
			</Menu>
		</React.Fragment>
	);
};

const getQuayPlanningDescription = (quayPlanning: LocationPlanningPlanboardQuayPlanningFragment) => {
	return [
		quayPlanning.cargoAction.call?.voyage?.creditor?.name ?? undefined,
		quayPlanning.cargoAction.cargo.product?.name ?? undefined,
		quayPlanning.cargoAction.call?.voyage?.ship?.name ?? undefined,
	].filter(it => it !== undefined).join(" - ");
};

export const createAvailabilityTasks = (
	availabilities: LocationPlanningLocationAvailabilityFragment[],
	onClick: (availability: LocationPlanningLocationAvailabilityFragment) => void,
) => {

	const tasks = [] as Item[];


	availabilities.forEach((availability: LocationPlanningLocationAvailabilityFragment) => {
		const description = intl.formatMessage({ id: availability.type });

		tasks.push({
			id: `availability-${availability.id}`,
			group: `location_${availability.location.id}_quay_${availability.bolderFrom.quay?.id}`,
			start: moment(availability.fromDateTime),
			end: moment(availability.toDateTime),
			className: "availability",
			description: description,
			title: (
				<div onClick={() => onClick(availability)} title={description}>
					{description}
				</div>
			),
		});
	});

	return tasks;
};

export const createClosingHoursTasks = (locations: LocationFromQuery[], window: Window) => {

	if (window.from === null || window.to === null) {
		return [];
	}

	const tasks = [] as Item[];
	const amountOfDays = moment(window.to).diff(moment(window.from), "days");

	const days: { [key: string]: number } = {
		SUNDAY: 0,
		MONDAY: 1,
		TUESDAY: 2,
		WEDNESDAY: 3,
		THURSDAY: 4,
		FRIDAY: 5,
		SATURDAY: 6,
	};

	locations.forEach(location => {
		if (location.openingHours) {
			[ ...Array(amountOfDays).keys() ].map((index) => {
				Object.keys(days).forEach((key) => {
					const date = moment(window.from).add(index, "days").toDate();
					const isDay = new Date(date).getDay() === days[key];

					if (isDay) {

						const from = location.openingHours?.[`${key.toLowerCase() as Day}From`];
						const to = location.openingHours?.[`${key.toLowerCase() as Day}To`];

						if (from !== null && from !== undefined && from >= 0) {

							const start = moment(date).startOf("day");
							const end = moment(date).startOf("day").add(from, "minutes");

							tasks.push({
								id: shortid.generate(),
								group: location.id,
								start: start,
								end: end,
								className: "closingHours",
								title: getClosingHoursString(start, end),
							});
						}

						if (to !== null && to !== undefined && to < 1440) {

							const start = moment(date).startOf("day").add(to, "minutes");
							const end = moment(date).endOf("day");

							tasks.push({
								id: shortid.generate(),
								group: location.id,
								start: start,
								end: end,
								className: "closingHours",
								title: getClosingHoursString(start, end),
							});
						}
					}
				});
			});
		}
	});

	return tasks;
};

const getClosingHoursString = (start: Moment, end: Moment) => {
	return [
		intl.formatMessage({ id: "STANDARD_CLOSINGS_CLOSE_FROM" }),
		moment(start).format("HH:mm"),
		intl.formatMessage({ id: "TO_LOWERCASE" }),
		moment(end).format("HH:mm"),
	].join(" ");
};

export const getTimeWindowsPerDay = (from: DateProperty, to: DateProperty) => {
	const amountOfDays = moment(to).diff(from, "days") + 1;

	return [ ...Array(amountOfDays).keys() ].map(index => ({
		from: DateUtils.getTimestamp(moment(from).add(index, "days")),
		to: DateUtils.getTimestamp(moment(from).add(index, "days").endOf("day")),
	}));
};

